/**
 * LightsPlacementTicker — the rolling autocue on OML arrival.
 * ─────────────────────────────────────────────────────────────────
 * Feb 27 2026 · Michael's brief:
 *
 *   "Let's bring back the rolling autocue counter:
 *    'Michael has just placed 3 lights in England'
 *    Let's have say 50 ranging from 1 light to 100 lights, random
 *    amounts, random countries in Europe, Americas and Asia."
 *
 * Renders as a single-line rolling ticker fixed low on the OML hero,
 * fading in and out entry-by-entry every ~4.5 s. Zero JS animation
 * cost — a CSS transition drives it. Order is shuffled per session
 * so the same three names don't lead every visit.
 *
 * Data source:  GET /api/one-million-lights/placement-ticker
 * ────────────────────────────────────────────────────────────── */
import React from "react";

const API = process.env.REACT_APP_BACKEND_URL || "";

const TOK = {
  cream: "rgba(245,236,217,0.94)",
  creamMist: "rgba(245,236,217,0.55)",
  gold: "#e0a97a",
  goldMist: "rgba(224,169,122,0.35)",
};

/** Shuffle an array (Fisher-Yates) — session-local so the ticker
 * doesn't lead with the same name every visit. */
function shuffle(arr) {
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

export default function LightsPlacementTicker({ intervalMs = 4500 }) {
  const [items, setItems] = React.useState([]);
  const [idx, setIdx] = React.useState(0);
  const [visible, setVisible] = React.useState(true);

  React.useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const r = await fetch(`${API}/api/one-million-lights/placement-ticker`);
        if (!r.ok) return;
        const d = await r.json();
        if (!mounted) return;
        // Real placements come first (server-side ordered), we shuffle
        // only the seed tail so real users always lead when present.
        const real = (d.placements || []).filter((p) => p.kind === "real");
        const seed = shuffle((d.placements || []).filter((p) => p.kind !== "real"));
        setItems([...real, ...seed]);
      } catch (_) { /* soft-fail — ticker just doesn't render */ }
    })();
    return () => { mounted = false; };
  }, []);

  React.useEffect(() => {
    if (items.length === 0) return;
    // Fade out → advance → fade in, on a fixed cadence.
    const cycle = setInterval(() => {
      setVisible(false);
      setTimeout(() => {
        setIdx((n) => (n + 1) % items.length);
        setVisible(true);
      }, 350);
    }, intervalMs);
    return () => clearInterval(cycle);
  }, [items, intervalMs]);

  if (items.length === 0) return null;
  const p = items[idx];
  const lightsWord = p.quantity === 1 ? "light" : "lights";

  return (
    <div
      className="w-full flex items-center justify-center gap-2 px-3"
      style={{ pointerEvents: "none", minHeight: 24 }}
      data-testid="oml-placement-ticker"
      aria-live="polite"
    >
      <span
        aria-hidden
        style={{
          display: "inline-block",
          width: 6,
          height: 6,
          borderRadius: "50%",
          background: TOK.gold,
          boxShadow: `0 0 10px ${TOK.gold}`,
          opacity: 0.9,
          flex: "0 0 auto",
        }}
      />
      <span
        style={{
          fontFamily: '"Cormorant Garamond", serif',
          fontStyle: "italic",
          fontSize: "clamp(0.82rem, 3.4vw, 0.94rem)",
          color: TOK.cream,
          letterSpacing: "0.005em",
          textAlign: "center",
          lineHeight: 1.35,
          opacity: visible ? 1 : 0,
          transition: "opacity 350ms ease",
          textShadow: "0 0 12px rgba(224,169,122,0.18)",
          maxWidth: "min(92vw, 480px)",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        <span style={{ color: TOK.gold, fontStyle: "normal", fontWeight: 500 }}>{p.name}</span>
        {" just placed "}
        <span style={{ color: TOK.gold, fontStyle: "normal", fontWeight: 500 }}>{p.quantity}</span>
        {" " + lightsWord + " in "}
        <span style={{ color: TOK.cream, fontStyle: "italic" }}>{p.country}</span>
      </span>
    </div>
  );
}
